import DeleteAccountButton from '../../components/DeleteAccountButton';
import PoweredByFHO from '../../components/PoweredByFHO';
import { getPartnerTheme } from '../../config/partnerThemes';
import { prepareCoachVoice, loadCoachVoices, saveCoachVoice, previewCoachVoice, playCoachVoice, stopCoachVoice, coachVoiceActive } from '../../utils/coachVoice';
import React, { useState, useEffect, useRef } from 'react';
import ConversationActions from '../../components/ConversationActions';
import PaywallModal from '../../components/auth/PaywallModal';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api_config';
import ResponseCounter from '../../components/auth/ResponseCounter';
import FeedbackButton from '../../components/FeedbackButton';
import { Send } from 'lucide-react';

// ============================================================================
// MENTAL PERFORMANCE THEME - Energy, Focus, Achievement
// ============================================================================
const PERF_THEME = {
  // Base backgrounds - Warmer dark navy (not cold purple)
  pageBg: '#1A1D2E',
  pageBgGradient: 'linear-gradient(135deg, #1A1D2E 0%, #151722 100%)',
  cardBg: '#252A3D',
  sidebarBg: '#1F2335',
  inputBg: 'rgba(255, 255, 255, 0.05)',
  
  // CHAMP theme (Faith-based) - Gold/Amber
  champ: {
    primary: '#F59E0B',
    primaryLight: '#FCD34D',
    gradient: 'linear-gradient(135deg, #F59E0B, #FCD34D)',
    text: '#000000',
    accent: '#FBBF24',
  },
  
  // SHIP theme (Self-mastery) - Deep teal/blue
  ship: {
    primary: '#0EA5E9',
    primaryLight: '#38BDF8',
    gradient: 'linear-gradient(135deg, #0EA5E9, #38BDF8)',
    text: '#FFFFFF',
    accent: '#22D3EE',
  },
  
  // Text colors
  textPrimary: '#F8FAFC',
  textSecondary: 'rgba(248, 250, 252, 0.7)',
  textMuted: 'rgba(248, 250, 252, 0.5)',
  
  // Borders
  border: 'rgba(255, 255, 255, 0.1)',
  borderLight: 'rgba(255, 255, 255, 0.05)',
  
  // Special states
  listening: 'linear-gradient(135deg, #f43f5e, #e11d48)',
  success: '#10b981',
  danger: '#dc3545',
};

// Color schemes for the stand-alone app: masculine, neutral, feminine, and red, black and white.
// Only the backgrounds change; CHAMP gold and SHIP blue stay as they are.
const MP_SCHEMES = {
  midnight: { id: 'midnight', label: 'Midnight', note: 'Deep navy', mark: '#4F6BBF', pageBg: '#1A1D2E', pageBgGradient: 'linear-gradient(135deg, #1A1D2E 0%, #151722 100%)', cardBg: '#252A3D', sidebarBg: '#1F2335' },
  graphite: { id: 'graphite', label: 'Graphite', note: 'Soft charcoal', mark: '#9AA0A8', pageBg: '#1C1E21', pageBgGradient: 'linear-gradient(135deg, #1C1E21 0%, #141618 100%)', cardBg: '#2A2D31', sidebarBg: '#232629' },
  plum: { id: 'plum', label: 'Plum', note: 'Warm plum', mark: '#A274B0', pageBg: '#251B2A', pageBgGradient: 'linear-gradient(135deg, #251B2A 0%, #1C1420 100%)', cardBg: '#35283B', sidebarBg: '#2D2132' },
  scarlet: { id: 'scarlet', label: 'Scarlet', note: 'Red, black and white', mark: '#E03A3E', pageBg: '#000000', pageBgGradient: 'radial-gradient(120% 70% at 50% 0%, rgba(224,58,62,0.28) 0%, #000000 62%)', cardBg: '#161616', sidebarBg: '#0B0B0B', border: 'rgba(224,58,62,0.45)' },
};
const MP_BASE_BORDER = PERF_THEME.border;
const applyMpScheme = (id) => {
  const s = MP_SCHEMES[id] || MP_SCHEMES.midnight;
  PERF_THEME.pageBg = s.pageBg; PERF_THEME.pageBgGradient = s.pageBgGradient;
  PERF_THEME.cardBg = s.cardBg; PERF_THEME.sidebarBg = s.sidebarBg;
  PERF_THEME.border = s.border || MP_BASE_BORDER;
  return s.id;
};

const MentalPerformanceChat = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [showPaywall, setShowPaywall] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [responsesRemaining, setResponsesRemaining] = useState(null);
  const [profile, setProfile] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showProfileDetails, setShowProfileDetails] = useState(false);
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [showConversationHistory, setShowConversationHistory] = useState(false);
  const [mpConversations, setMpConversations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  // Stand-alone app (access code): no links to other apps, voice and color choices, credit line.
  const mpAlone = getPartnerTheme().id === 'mp';
  const [mpScheme, setMpScheme] = useState(() => {
    if (!mpAlone) return 'midnight';
    let s = null;
    try { s = localStorage.getItem('mpColorScheme'); } catch (e) { /* storage blocked */ }
    return applyMpScheme(s);
  });
  const [voiceList, setVoiceList] = useState([]);
  const [voiceChoice, setVoiceChoice] = useState(null);
  // PLATFORM_VOICE_V1: the main Platform gets the voice choice too
  useEffect(() => {
    if (mpAlone) return;
    prepareCoachVoice();
    loadCoachVoices().then((d) => { if (d) { setVoiceList(d.voices); setVoiceChoice(d.choice); } });
  }, []);
  useEffect(() => {
    if (!mpAlone) return;
    prepareCoachVoice();
    loadCoachVoices().then((d) => { if (d) { setVoiceList(d.voices); setVoiceChoice(d.choice); } });
    API.get('/api/v1/mental-performance/color-scheme').then((r) => {
      const id = r.data && r.data.active;
      if (id && MP_SCHEMES[id]) {
        try { localStorage.setItem('mpColorScheme', id); } catch (e) { /* storage blocked */ }
        setMpScheme(applyMpScheme(id));
      }
    }).catch(() => {});
  }, []);
  const chooseScheme = (id) => {
    try { localStorage.setItem('mpColorScheme', id); } catch (e) { /* storage blocked */ }
    setMpScheme(applyMpScheme(id));
    API.post('/api/v1/mental-performance/color-scheme', { scheme: id }).catch(() => {});
  };

  // Check for pending cross-app prompt after profile loads
  useEffect(() => {
    if (profile) {
      const pendingPrompt = localStorage.getItem('mentalPerformance_pendingPrompt');
      if (pendingPrompt) {
        console.log('📬 Found pending prompt for CHAMP:', pendingPrompt);
        localStorage.removeItem('mentalPerformance_pendingPrompt');
        setInput(pendingPrompt);
        setTimeout(() => {
          sendMessage(pendingPrompt);
        }, 1000);
      }
    }
  }, [profile]);

  // CHAMP Topics (Faith-based)
  const champTopics = [
    // General Performance (7)
    "Pre-game nerves and anxiety",
    "Building confidence through faith",
    "Bouncing back after mistakes",
    "Competing for God's glory",
    "Fear of failure",
    "Focus and concentration",
    "Handling pressure moments",
    // FHO Framework Topics (8 - 53%)
    "Activating Your Power MindStream",
    "Using the 12 Life Choices for Peak Performance",
    "Turning On Your Antigravity Relay",
    "Freedom vs Dependency in Competition",
    "Becoming a Fholific Competitor",
    "Choosing Happiness Over Despair in Competition",
    "Building Your I-THEE Connection for Power",
    "Flow State Through the I-ME Relationship"
  ];

  // SHIP Topics (Self-mastery)
  const shipTopics = [
    // General Performance (7)
    "Pre-game nerves and anxiety",
    "Building unshakeable confidence",
    "Bouncing back after mistakes",
    "Accessing flow state",
    "Fear of failure",
    "Focus and concentration",
    "Handling pressure moments",
    // FHO Framework Topics (8 - 53%)
    "Activating Your Power MindStream",
    "Using the 12 Life Choices for Peak Performance",
    "Turning On Your Antigravity Relay",
    "Breaking Free from the 3D Swamp Mindset",
    "Autocebos for Mental Performance",
    "Choosing Happiness Over Despair",
    "Strengthening Your I-ME for Flow",
    "Clarity Through I-WE Relationships"
  ];

  const isChamp = profile?.program === 'champ';
  const topics = isChamp ? champTopics : shipTopics;
  
  // Get current theme based on program
  const currentTheme = isChamp ? PERF_THEME.champ : PERF_THEME.ship;

  // ============ Voice Hook ============
  const useVoiceChat = () => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [voiceEnabled, setVoiceEnabled] = useState(() => {
      const saved = localStorage.getItem('mentalPerfVoiceEnabled');
      return saved ? JSON.parse(saved) : false;
    });
    const [isSpeaking, setIsSpeaking] = useState(false);
    
    const recognitionRef = useRef(null);
    const synthRef = useRef(window.speechSynthesis);
    
    const isSupported = typeof window !== 'undefined' && 
      ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
    
    useEffect(() => {
      if (!isSupported) return;
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalTranscript += t;
        }
        if (finalTranscript) setTranscript(finalTranscript);
      };
      
      recognitionRef.current.onend = () => setIsListening(false);
      recognitionRef.current.onerror = () => setIsListening(false);


  return () => { if (recognitionRef.current) recognitionRef.current.abort(); };
    }, [isSupported]);
    
    const startListening = () => {
      if (!isSupported || !recognitionRef.current) return;
      setTranscript('');
      setIsListening(true);
      try { recognitionRef.current.start(); } catch (e) {}
    };
    
    const stopListening = () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      setIsListening(false);
    };
    
    const stripMarkdownForVoice = (text) => {
      return text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/_([^_]+)_/g, '$1')
        .replace(/FHO-OS/gi, 'foe OS')
        .replace(/FHO/gi, 'foe');
    };

    const speak = (text) => {
      if (!voiceEnabled) return;
      if (coachVoiceActive()) {
        if (synthRef.current) synthRef.current.cancel();
        playCoachVoice(text, () => setIsSpeaking(true), () => setIsSpeaking(false), (rest) => speakDevice(rest));
        return;
      }
      speakDevice(text);
    };
    const speakDevice = (text) => {
      if (!synthRef.current || !voiceEnabled) return;
      synthRef.current.cancel();
      const cleanText = stripMarkdownForVoice(text);
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.rate = 0.90;
      utterance.pitch = 0.85;
      const voices = synthRef.current.getVoices();
      const preferredVoice = voices.find(v => 
        v.name.includes('Samantha') || v.name.includes('Karen') || v.name.includes('Victoria')
      );
      if (preferredVoice) utterance.voice = preferredVoice;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      synthRef.current.speak(utterance);
    };
    
    const stopSpeaking = () => {
      stopCoachVoice();
      if (synthRef.current) {
        synthRef.current.cancel();
        setIsSpeaking(false);
      }
    };
    
    const toggleVoice = () => {
      const newValue = !voiceEnabled;
      setVoiceEnabled(newValue);
      localStorage.setItem('mentalPerfVoiceEnabled', JSON.stringify(newValue));
      if (!newValue) stopSpeaking();
    };
    
    const setVoiceOn = (on) => {
      setVoiceEnabled(on);
      localStorage.setItem('mentalPerfVoiceEnabled', JSON.stringify(on));
      if (!on) stopSpeaking();
    };
    return { isListening, transcript, voiceEnabled, isSpeaking, isSupported, startListening, stopListening, speak, stopSpeaking, toggleVoice, setVoiceOn };
  };

  const voice = useVoiceChat();

  useEffect(() => {
    if (voice.transcript) setInput(voice.transcript);
  }, [voice.transcript]);
  // Auto-send when browser silence detection cuts mic
  const wasListeningRef = useRef(false);
  useEffect(() => {
    if (wasListeningRef.current && !voice.isListening && input.trim()) {
      setTimeout(() => sendMessage(), 150);
    }
    wasListeningRef.current = voice.isListening;
  }, [voice.isListening]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const promptParam = urlParams.get('prompt');
    const autoSend = urlParams.get('send') === 'true';
    
    if (promptParam) {
      const decodedPrompt = decodeURIComponent(promptParam);
      
      // Store in localStorage to survive profile setup redirect
      if (autoSend) {
        localStorage.setItem('mentalPerformance_pendingPrompt', decodedPrompt);
        console.log('📝 Stored pending prompt for CHAMP:', decodedPrompt);
      }
      
      setInput(decodedPrompt);
      window.history.replaceState({}, '', window.location.pathname);
      // Auto-send handled by pendingPrompt useEffect after profile loads
    }
  }, [profile]);

  // Journey Focus: Auto-pickup from Journey Dashboard Recommended Focus
  useEffect(() => {
    const journeyPrompt = localStorage.getItem('journeyFocusPrompt');
    if (journeyPrompt && profile) {
      localStorage.removeItem('journeyFocusPrompt');
      localStorage.removeItem('journeyFocusCategory');
      localStorage.removeItem('guidedSessionId');
      localStorage.removeItem('guidedSessionDimension');
      localStorage.removeItem('guidedSessionFocus');
      localStorage.removeItem('guidedSessionFollowUps');
      
      setInput(journeyPrompt);
      setTimeout(() => {
        sendMessage(journeyPrompt);
      }, 800);
    }
  }, [profile]);

  useEffect(() => { loadProfile(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadProfile = async () => {
    try {
      const res = await API.get('/api/v1/mental-performance/profile');
      if (!res.data.exists) {
        navigate('/mental-performance/setup');
        return;
      }
      setProfile(res.data);
      
      const name = res.data.preferred_name || 'champion';
      const domainLabels = {
        executive: "leadership", entrepreneur: "building your venture", 
        performer: "your craft", student: "academics", sales: "sales", 
        medical: "clinical excellence", educator: "shaping minds", legal: "your legal practice",
        communications: "communications leadership",
        coach: "your coaching"
      };
      const parts = [];
      if (res.data.performance_domain && res.data.performance_domain !== "athlete" && domainLabels[res.data.performance_domain]) {
        parts.push(domainLabels[res.data.performance_domain]);
      }
      if (res.data.sport && res.data.performance_domain === "athlete") {
        parts.push("elevating your " + res.data.sport);
      }
      const context = parts.length > 0 ? parts.join(", and ") : "peak performance";
      const programName = res.data.program === 'champ' ? 'CHAMP' : 'SHIP';
      
      setMessages([{
        role: 'assistant',
        content: `Welcome${name ? `, ${name}` : ''}! I'm your ${programName} Coach — here to help you master the mental game of ${context}.\n\n${res.data.program === 'champ' ? "Remember: God created you for excellence. Let's compete for His glory." : "Remember: Master yourself, master your game."}\n\nWhat's on your mind today?`
      }]);
    } catch (err) {
      navigate('/mental-performance/setup');
    }
  };

  // Load conversation history
  const loadMPHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await API.get("/api/v1/mental-performance/conversations");
      setMpConversations(res.data.conversations || []);
    } catch (error) {
      console.error("Error loading history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load specific conversation messages
  const loadConversationMessages = async (convId) => {
    try {
      const res = await API.get(`/api/v1/mental-performance/conversations/${convId}`);
      const loadedMessages = res.data.messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      setMessages(loadedMessages);
      setConversationId(convId);
      setShowConversationHistory(false);
    } catch (error) {
      console.error("Error loading conversation:", error);
    }
  };


  const switchProgram = async (newProgram) => {
    try {
      await API.post(`/api/v1/mental-performance/switch-program?program=${newProgram}`);
      setProfile({ ...profile, program: newProgram });
      setMessages([]);
      setConversationId(null);
      loadProfile();
    } catch (err) {
      console.error('Error switching program:', err);
    }
  };

  const sendMessage = async (directPrompt = null) => {
    const messageToSend = (typeof directPrompt === 'string') ? directPrompt : input.trim();
    if (!messageToSend || isLoading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: messageToSend }]);
    setIsLoading(true);
    setShowTopicsModal(false);
    setShowSidebar(false);

    setMessages(prev => [...prev, { role: 'assistant', content: '', isStreaming: true }]);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = window.location.origin;

      const response = await fetch(`${apiUrl}/api/v1/mental-performance/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ partner: getPartnerTheme().id, message: messageToSend, conversation_id: conversationId })
      });

      // Check for free tier exhausted
      if (response.status === 403) {
        setShowPaywall(true);
        setMessages(prev => prev.slice(0, -1)); // Remove the empty streaming message
        setIsLoading(false);
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';
      let accumulatedText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) {
                accumulatedText += data.text;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: accumulatedText, isStreaming: true };
                  return updated;
                });
              }
              if (data.conversation_id) setConversationId(data.conversation_id);
              if (data.responses_remaining !== undefined) {
                setResponsesRemaining(data.responses_remaining);
                // Show paywall if exhausted (same as Emit)
                if (data.responses_remaining === 0) {
                  setTimeout(() => setShowPaywall(true), 2000);
                }
              }
              if (data.done) {
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: accumulatedText, isStreaming: false };
                  return updated;
                });
                if (voice.voiceEnabled && accumulatedText) {
                  setTimeout(() => voice.speak(accumulatedText), 300);
                }
              }
            } catch (e) {}
          }
        }
      }
    } catch (err) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: "I apologize, but I'm having trouble responding. Please try again.", isStreaming: false };
        return updated;
      });
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getLevelLabel = (level) => {
    const labels = { 'recreational': 'Recreational', 'high_school': 'High School', 'college': 'College', 'professional': 'Professional', 'masters': 'Masters/Adult' };
    return labels[level] || level;
  };

  const renderMessageContent = (text) => {
    // Stand-alone app: a link the coach still writes shows as its words only.
    if (mpAlone) return String(text || '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
      const linkText = match[1];
      const linkUrl = match[2];
      parts.push(
        <a key={match.index} href={linkUrl}
          style={{ color: currentTheme.accent, textDecoration: 'underline', cursor: 'pointer' }}
          onClick={(e) => { e.preventDefault(); window.location.href = linkUrl; }}
        >{linkText}</a>
      );
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 0 ? parts : text;
  };

  if (!profile) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: PERF_THEME.pageBgGradient, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        color: PERF_THEME.textPrimary 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '24px', height: '24px',
            border: '3px solid #0EA5E9',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span>Loading your training zone...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', height: '100vh',
      background: PERF_THEME.pageBgGradient,
      display: 'flex',
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      overflow: 'hidden'
    }}>
      {/* Sidebar Backdrop */}
      {showSidebar && (
        <div onClick={() => setShowSidebar(false)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 998
        }} />
      )}

      {/* Sidebar */}
      <div style={{
        position: 'fixed', top: 0,
        right: showSidebar ? 0 : '-280px',
        width: '280px', height: '100%',
        background: PERF_THEME.sidebarBg,
        transition: 'right 0.3s ease',
        overflow: 'auto',
        display: 'flex', flexDirection: 'column',
        zIndex: 999,
        boxShadow: showSidebar ? '-4px 0 20px rgba(0,0,0,0.4)' : 'none'
      }}>
        <div style={{ padding: '20px', minWidth: '280px' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{
              width: '48px', height: '48px',
              background: currentTheme.gradient,
              borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '24px',
              boxShadow: `0 4px 12px ${currentTheme.primary}40`
            }}>{isChamp ? '✝️' : '🚀'}</div>
            <div>
              <div style={{ color: PERF_THEME.textPrimary, fontWeight: '700', fontSize: '18px' }}>{isChamp ? 'CHAMP' : 'SHIP'} Coach</div>
              <div style={{ color: PERF_THEME.textMuted, fontSize: '12px', fontStyle: 'italic' }}>
                {isChamp ? '"Champions Through Faith"' : '"Master Your Game"'}
              </div>
            </div>
          </div>

          {/* Program Switcher */}
          <div style={{ marginBottom: '6px' }}>
            <div style={{ color: PERF_THEME.textMuted, fontSize: '11px', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '1px' }}>Program</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => switchProgram('champ')} style={{
                flex: 1, padding: '7px 12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600',
                background: isChamp ? PERF_THEME.champ.gradient : PERF_THEME.inputBg,
                color: isChamp ? PERF_THEME.champ.text : PERF_THEME.textSecondary,
                boxShadow: isChamp ? `0 2px 8px ${PERF_THEME.champ.primary}40` : 'none',
                transition: 'all 0.3s'
              }}>✝️ CHAMP</button>
              <button onClick={() => switchProgram('ship')} style={{
                flex: 1, padding: '7px 12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600',
                background: !isChamp ? PERF_THEME.ship.gradient : PERF_THEME.inputBg,
                color: !isChamp ? PERF_THEME.ship.text : PERF_THEME.textSecondary,
                boxShadow: !isChamp ? `0 2px 8px ${PERF_THEME.ship.primary}40` : 'none',
                transition: 'all 0.3s'
              }}>🚀 SHIP</button>
            </div>
          </div>

          {/* Profile Info - Collapsible */}
          <div style={{ marginBottom: '6px' }}>
            <button onClick={() => setShowProfileDetails(!showProfileDetails)} style={{
              width: '100%', padding: '8px',
              background: PERF_THEME.inputBg,
              border: `1px solid ${PERF_THEME.border}`,
              borderRadius: '10px',
              color: PERF_THEME.textPrimary,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: showProfileDetails ? '12px' : '0'
            }}>
              <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🏆</span> View Profile
              </span>
              <span style={{ transform: showProfileDetails ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
            </button>

            {showProfileDetails && (
              <div style={{ padding: '8px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: `1px solid ${PERF_THEME.borderLight}` }}>
                <div style={{ padding: '7px 12px', background: PERF_THEME.inputBg, borderRadius: '8px', marginBottom: '6px' }}>
                  <div style={{ color: PERF_THEME.textPrimary, fontWeight: '600', marginBottom: '2px' }}>{profile.preferred_name || 'Athlete'}</div>
                  <div style={{ color: PERF_THEME.textSecondary, fontSize: '13px' }}>{profile.sport} {profile.position ? `• ${profile.position}` : ''}</div>
                  <div style={{ color: PERF_THEME.textMuted, fontSize: '12px' }}>{getLevelLabel(profile.competitive_level)}</div>
                </div>
                {profile.goals?.length > 0 && (
                  <div style={{ marginBottom: '3px' }}>
                    <div style={{ color: PERF_THEME.textMuted, fontSize: '11px', marginBottom: '3px' }}>Goals</div>
                    <div style={{ color: PERF_THEME.textSecondary, fontSize: '12px' }}>{profile.goals.slice(0,3).join(', ')}</div>
                  </div>
                )}
                {profile.challenges?.length > 0 && (
                  <div>
                    <div style={{ color: PERF_THEME.textMuted, fontSize: '11px', marginBottom: '3px' }}>Challenges</div>
                    <div style={{ color: PERF_THEME.textSecondary, fontSize: '12px' }}>{profile.challenges.slice(0,3).join(', ')}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar Buttons */}
          <div style={{ paddingTop: '6px', borderTop: `1px solid ${PERF_THEME.border}` }}>
            <button onClick={() => { setShowSidebar(false); navigate('/mental-performance/setup'); }} style={{
              width: '100%', padding: '7px 12px',
              background: `${currentTheme.primary}20`,
              border: `1px solid ${currentTheme.primary}60`,
              borderRadius: '10px',
              color: currentTheme.primary,
              display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '12px', cursor: 'pointer', marginBottom: '3px', fontSize: '13px'
            }}>✏️ Edit Profile</button>

            <button onClick={() => { setShowSidebar(false); setShowTopicsModal(true); }} style={{
              width: '100%', padding: '8px',
              background: currentTheme.gradient,
              border: 'none', borderRadius: '10px',
              color: currentTheme.text,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '12px', gap: '8px',
              marginBottom: '3px', fontWeight: '600',
              boxShadow: `0 2px 8px ${currentTheme.primary}40`
            }}>💡 Explore Topics</button>

            <button onClick={() => { setShowSidebar(false); setMessages([]); setConversationId(null); }} style={{
              width: '100%', padding: '7px 12px',
              background: PERF_THEME.inputBg,
              border: `1px solid ${PERF_THEME.border}`,
              borderRadius: '10px',
              color: PERF_THEME.textSecondary,
              display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '12px', cursor: 'pointer', marginBottom: '3px', fontSize: '13px'
            }}>🔄 New Conversation</button>

            <button onClick={() => { setShowSidebar(false); loadMPHistory(); setShowConversationHistory(true); }} style={{
              width: '100%', padding: '7px 12px', background: 'transparent',
              border: `1px solid ${PERF_THEME.border}`, borderRadius: '10px',
              color: PERF_THEME.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '12px', cursor: 'pointer', marginBottom: '3px', fontSize: '13px'
            }}>📜 Conversation History</button>

            {mpAlone && (
              <div className="mp-menu-colors" style={{ padding: '10px 8px', background: PERF_THEME.inputBg, border: `1px solid ${PERF_THEME.border}`, borderRadius: '10px', marginBottom: '6px' }}>
                <div style={{ color: PERF_THEME.textPrimary, fontSize: '15px', fontWeight: 600, margin: '0 4px 8px' }}>Colors</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {Object.values(MP_SCHEMES).map((sc) => {
                    const on = mpScheme === sc.id;
                    return (
                      <button key={sc.id} type="button" onClick={() => chooseScheme(sc.id)} aria-pressed={on}
                        style={{ minWidth: 0, minHeight: '64px', padding: '8px 4px', background: sc.pageBg, border: `2px solid ${on ? currentTheme.primary : 'rgba(255,255,255,0.18)'}`, borderRadius: '10px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px', height: '16px' }}>
                          <span style={{ display: 'flex', width: '40px', height: '14px', borderRadius: '7px', overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.2)' }}>
                            <span style={{ flex: 1, background: sc.cardBg }} />
                            <span style={{ flex: 1, background: sc.mark }} />
                          </span>
                          {on && <span style={{ color: '#FFFFFF', fontSize: '15px', lineHeight: 1 }}>{'\u2713'}</span>}
                        </span>
                        <span style={{ color: '#FFFFFF', fontSize: '15px', lineHeight: 1.2, fontWeight: on ? 700 : 500 }}>{sc.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {voiceList.length > 1 && (
              <div style={{ padding: '10px 8px', background: PERF_THEME.inputBg, border: `1px solid ${PERF_THEME.border}`, borderRadius: '10px', marginBottom: '6px' }}>
                <div style={{ color: PERF_THEME.textPrimary, fontSize: '15px', fontWeight: 600, margin: '0 4px 8px' }}>Voice</div>
                <div role="radiogroup" aria-label="Voice" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {voiceList.map((v) => {
                    const on = voiceChoice === v.id;
                    return (
                      <div key={v.id} style={{ display: 'flex', alignItems: 'stretch', gap: '6px' }}>
                        <button type="button" role="radio" aria-checked={on} onClick={() => { setVoiceChoice(v.id); saveCoachVoice(v.id); voice.setVoiceOn(true); }}
                          style={{ flex: 1, minWidth: 0, padding: '8px 10px', background: on ? `${currentTheme.primary}26` : 'transparent', border: `2px solid ${on ? currentTheme.primary : PERF_THEME.border}`, borderRadius: '10px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                          <span style={{ display: 'block', color: PERF_THEME.textPrimary, fontSize: '15px', fontWeight: on ? 700 : 500, lineHeight: 1.3 }}>{on ? '\u2713 ' : ''}{v.label}</span>
                          {v.note && <span style={{ display: 'block', color: PERF_THEME.textSecondary, fontSize: '14px', lineHeight: 1.3, marginTop: '2px' }}>{v.note}</span>}
                        </button>
                        <button type="button" onClick={() => previewCoachVoice(v.id)} aria-label={'Hear ' + v.label} title={'Hear ' + v.label}
                          style={{ width: '44px', flex: '0 0 44px', background: 'transparent', border: `1px solid ${PERF_THEME.border}`, borderRadius: '10px', color: PERF_THEME.textPrimary, fontSize: '16px', cursor: 'pointer' }}>
                          {'\u25B6'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!mpAlone && (<>
            {/* ── Change Home App ── */}
            <button onClick={() => { setShowSidebar(false); navigate('/select-home-app'); }} style={{
              width: '100%', padding: '7px 12px',
              background: `${currentTheme.primary}20`,
              border: `1px solid ${currentTheme.primary}60`,
              borderRadius: '10px',
              color: currentTheme.primary,
              display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '12px', cursor: 'pointer', marginBottom: '3px', fontSize: '13px', fontWeight: '600'
            }}>🏠 Change Home App</button>

            <button onClick={() => { setShowSidebar(false); navigate('/emit-the-bible'); }} style={{
              width: '100%', padding: '7px 12px', background: 'transparent',
              border: `1px solid ${PERF_THEME.border}`, borderRadius: '10px',
              color: PERF_THEME.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '12px', cursor: 'pointer', marginBottom: '3px', fontSize: '13px'
            }}>✨ Emit / 💫 SAGE</button>

            <button onClick={() => { setShowSidebar(false); navigate('/family-coach'); }} style={{
              width: '100%', padding: '7px 12px', background: 'transparent',
              border: `1px solid ${PERF_THEME.border}`, borderRadius: '10px',
              color: PERF_THEME.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '12px', cursor: 'pointer', marginBottom: '3px', fontSize: '13px'
            }}>👨‍👩‍👧 Family Coach</button>
            
            <button onClick={() => { setShowSidebar(false); navigate('/safe'); }} style={{
              width: '100%', padding: '7px 12px', background: 'transparent',
              border: `1px solid ${PERF_THEME.border}`, borderRadius: '10px',
              color: PERF_THEME.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '12px', cursor: 'pointer', marginBottom: '3px', fontSize: '13px'
            }}>🛡️ SAFE Buddy</button>
            
            <button onClick={() => { setShowSidebar(false); navigate('/journey'); }} style={{
              width: '100%', padding: '7px 12px',
              background: `linear-gradient(135deg, ${currentTheme.primary}30, ${currentTheme.primaryLight}30)`,
              border: `1px solid ${currentTheme.accent}50`,
              borderRadius: '10px',
              color: currentTheme.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '12px', cursor: 'pointer', marginBottom: '3px', fontSize: '13px'
            }}>✨ My Journey</button>

            </>)}
            <button onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/login'; }} style={{
              width: '100%', padding: '7px 12px',
              background: 'rgba(220,53,69,0.15)',
              border: '1px solid rgba(220,53,69,0.3)',
              borderRadius: '10px',
              color: '#ff6b6b',
              cursor: 'pointer', fontSize: '13px'
            }}>🚪 Logout</button>
            <DeleteAccountButton variant="dark" />
            {mpAlone && (<div style={{ padding: '14px 4px 4px', fontSize: '14px', color: PERF_THEME.textSecondary, textAlign: 'center' }}>Built by MindStreams Technology</div>)}
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${PERF_THEME.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'rgba(37, 42, 61, 0.5)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px',
              background: currentTheme.gradient,
              borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '20px',
              boxShadow: `0 2px 8px ${currentTheme.primary}40`
            }}>{isChamp ? '✝️' : '🚀'}</div>
            <div>
              <div style={{ color: PERF_THEME.textPrimary, fontWeight: '600', fontSize: '18px' }}>{isChamp ? 'CHAMP' : 'SHIP'} Coach</div>
              <div style={{ color: PERF_THEME.textMuted, fontSize: '13px' }}>{profile.sport || 'Peak Performance'}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <PoweredByFHO />
            <button onClick={() => setShowSidebar(!showSidebar)} style={{
            background: 'none', border: 'none', color: PERF_THEME.textPrimary, fontSize: '24px', cursor: 'pointer', padding: '4px'
          }}>☰</button>
          </div>
        </div>
        
        {/* Free Trial Counter */}
        {responsesRemaining !== null && (
          <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 1000 }}>
            <ResponseCounter responsesRemaining={responsesRemaining} />
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {messages.map((msg, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '75%', padding: '14px 18px',
                borderRadius: msg.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                background: msg.role === 'user' ? currentTheme.gradient : PERF_THEME.inputBg,
                color: msg.role === 'user' ? currentTheme.text : PERF_THEME.textPrimary,
                whiteSpace: 'pre-wrap', lineHeight: '1.6',
                boxShadow: msg.role === 'user' 
                  ? `0 2px 8px ${currentTheme.primary}30` 
                  : '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                {msg.isStreaming ? msg.content : renderMessageContent(msg.content)}
                {msg.isStreaming && (
                  <span style={{ 
                    display: 'inline-block', width: '8px', height: '8px',
                    background: currentTheme.primary, borderRadius: '50%',
                    marginLeft: '8px', animation: 'pulse 1s infinite'
                  }}></span>
                )}
                {msg.role === 'assistant' && !msg.isStreaming && idx > 0 && idx === messages.length - 1 && (
                  <FeedbackButton sessionId={`mp_${Date.now()}`} messageIndex={idx} />
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area with Voice */}
        <div style={{
          padding: '12px 16px',
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          borderTop: `1px solid ${PERF_THEME.border}`,
          background: PERF_THEME.pageBg
        }}>
          <div style={{ marginBottom: '6px' }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Ask your ${isChamp ? 'CHAMP' : 'SHIP'} Coach...`}
              rows={1}
              style={{
                width: '100%', padding: '14px 16px',
                background: PERF_THEME.inputBg,
                border: `1px solid ${PERF_THEME.border}`,
                borderRadius: '16px',
                color: PERF_THEME.textPrimary,
                fontSize: '16px', resize: 'none', outline: 'none',
                minHeight: '48px', maxHeight: '120px', boxSizing: 'border-box',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = currentTheme.primary}
              onBlur={(e) => e.target.style.borderColor = PERF_THEME.border}
            />
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
            {voice.isSupported && (
              <button
                type="button"
                onClick={() => {
                  if (voice.isListening) {
                    voice.stopListening();
                    if (voice.transcript.trim()) setTimeout(() => sendMessage(), 100);
                  } else {
                    voice.startListening();
                  }
                }}
                disabled={isLoading}
                style={{
                  padding: '8px',
                  background: voice.isListening ? PERF_THEME.listening : currentTheme.gradient,
                  border: 'none', borderRadius: '50%',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '20px', width: '48px', height: '48px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: isLoading ? 0.5 : 1,
                  boxShadow: voice.isListening 
                    ? '0 0 0 3px rgba(244,63,94,0.3)' 
                    : `0 2px 8px ${currentTheme.primary}40`,
                  transition: 'all 0.3s'
                }}
                title={voice.isListening ? 'Click to stop and send' : 'Click to speak'}
              >
                {voice.isListening ? '⏹️' : '🎤'}
              </button>
            )}

            {voice.isSupported && (
              <button
                type="button"
                onClick={() => voice.toggleVoice()}
                disabled={isLoading}
                style={{
                  padding: '8px',
                  background: voice.voiceEnabled 
                    ? `linear-gradient(135deg, ${PERF_THEME.success}, #059669)` 
                    : PERF_THEME.inputBg,
                  border: voice.voiceEnabled ? 'none' : `1px solid ${PERF_THEME.border}`,
                  borderRadius: '50%',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '20px', width: '48px', height: '48px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: isLoading ? 0.5 : 1,
                  transition: 'all 0.3s'
                }}
                title={voice.voiceEnabled ? 'Disable voice responses' : 'Enable voice responses'}
              >
                {voice.voiceEnabled ? '🔊' : '🔇'}
              </button>
            )}

            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading}
              style={{
                width: '48px', height: '48px', borderRadius: '50%',
                background: input.trim() ? 'linear-gradient(135deg, #3B82F6, #60A5FA)' : PERF_THEME.inputBg,
                border: 'none',
                color: input.trim() ? '#fff' : PERF_THEME.textMuted,
                fontSize: '20px',
                cursor: input.trim() ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: input.trim() ? '0 4px 15px rgba(59, 130, 246, 0.5)' : 'none',
                transition: 'all 0.3s'
              }}
            ><Send size={20} /></button>
          </div>
          
          {voice.isListening && (
            <div style={{
              marginTop: '8px', textAlign: 'center', color: '#f43f5e', fontSize: '13px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}>
              <span style={{ width: '8px', height: '8px', background: '#f43f5e', borderRadius: '50%', animation: 'pulse 1s infinite' }}></span>
              Listening...
            </div>
          )}
        </div>
      </div>

      {/* Topics Modal */}
      {showTopicsModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '20px'
        }} onClick={() => setShowTopicsModal(false)}>
          <div style={{
            background: PERF_THEME.cardBg,
            borderRadius: '20px', maxWidth: '600px', width: '100%',
            maxHeight: '80vh', overflow: 'auto', padding: '24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '28px' }}>{isChamp ? '✝️' : '🚀'}</span>
                <div>
                  <h2 style={{ color: PERF_THEME.textPrimary, margin: 0, fontSize: '20px' }}>
                    {isChamp ? 'CHAMP' : 'SHIP'} Topics
                  </h2>
                  <p style={{ color: PERF_THEME.textMuted, margin: 0, fontSize: '13px' }}>
                    Select a topic to explore
                  </p>
                </div>
              </div>
              <button onClick={() => setShowTopicsModal(false)} style={{
                background: 'none', border: 'none', color: PERF_THEME.textMuted, fontSize: '28px', cursor: 'pointer'
              }}>×</button>
            </div>
            <div style={{ display: 'grid', gap: '10px' }}>
              {topics.map((topic, idx) => (
                <button key={idx} onClick={() => sendMessage(topic)} style={{
                  padding: '14px 18px',
                  background: PERF_THEME.inputBg,
                  border: `1px solid ${PERF_THEME.border}`,
                  borderRadius: '10px',
                  color: PERF_THEME.textPrimary,
                  cursor: 'pointer', textAlign: 'left', fontSize: '13px',
                  transition: 'all 0.2s ease'
                }} 
                onMouseOver={(e) => {
                  e.target.style.background = `${currentTheme.primary}20`;
                  e.target.style.borderColor = currentTheme.primary;
                }}
                onMouseOut={(e) => {
                  e.target.style.background = PERF_THEME.inputBg;
                  e.target.style.borderColor = PERF_THEME.border;
                }}
                >{topic}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.95); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        html, body {
          background: ${PERF_THEME.pageBg} !important;
        }
        ::placeholder {
          color: ${PERF_THEME.textMuted};
        }
      `}</style>

      {/* Conversation History Modal */}
      {showConversationHistory && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: PERF_THEME.cardBg, borderRadius: '16px', width: '90%', maxWidth: '500px',
            maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            border: `1px solid ${PERF_THEME.border}`
          }}>
            <div style={{
              padding: '20px', borderBottom: `1px solid ${PERF_THEME.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: PERF_THEME.textPrimary }}>📜 Conversation History</h2>
              <button onClick={() => setShowConversationHistory(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: PERF_THEME.textMuted }}>×</button>
            </div>
            <div style={{ padding: '16px', maxHeight: '50vh', overflowY: 'auto' }}>
              {loadingHistory ? (
                <p style={{ color: PERF_THEME.textMuted, textAlign: 'center' }}>Loading conversations...</p>
              ) : mpConversations.length === 0 ? (
                <p style={{ color: PERF_THEME.textMuted, textAlign: 'center' }}>No conversations yet</p>
              ) : (
                mpConversations.map((conv, idx) => (
                  <div key={conv.conversation_id || idx} onClick={() => loadConversationMessages(conv.conversation_id)}
                    style={{
                      padding: '16px', marginBottom: '6px', borderRadius: '12px',
                      background: conversationId === conv.conversation_id ? 'rgba(59,130,246,0.2)' : PERF_THEME.inputBg,
                      cursor: 'pointer', border: conversationId === conv.conversation_id ? '2px solid #3b82f6' : '2px solid transparent',
                      transition: 'all 0.2s ease'
                    }}>
                    <div style={{ fontWeight: '600', color: PERF_THEME.textPrimary, marginBottom: '3px' }}>
                      {conv.title || 'Conversation'}
                    </div>
                    <div style={{ fontSize: '13px', color: PERF_THEME.textMuted }}>
                      {conv.program?.toUpperCase()} • {conv.last_message && new Date(conv.last_message).toLocaleDateString()}
                    </div>
                      <ConversationActions app="mental-performance" color={PERF_THEME.textMuted} conversationId={conv.conversation_id} onDeleted={(id) => { setMpConversations(prev => prev.filter(c => c.conversation_id !== id)); if (conversationId === id) { setConversationId(null); setMessages([]); } }} />
                  </div>
                ))
              )}
            </div>
            <div style={{ padding: '16px', borderTop: `1px solid ${PERF_THEME.border}`, textAlign: 'center' }}>
              <button onClick={() => { setConversationId(null); setMessages([]); setShowConversationHistory(false); loadProfile(); }}
                style={{ padding: '12px 24px', background: currentTheme.gradient, color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                + Start New Conversation
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}
    </div>
  );
};

export default MentalPerformanceChat;
